import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';

import { CONFIRM_STOCK_LEVELS, REPORT_INCORRECT_STOCK_SETTING } from '../../graphql/serviceDocket';
import { POS_TYPE } from '../../constants';

import { getFullMachineName } from '../machine';
import { useDidUpdateEffect, useEventCallback, useForm } from '../hooks';
import { getAdjustedExpiryDatesByQuantityChange, sortExpiryDatesByDate, updateExpiryDates } from '../planogramItem';
import { calculatePlanogramItemsPositions } from '../planograms/planogram';
import { valueExists } from '../utils';
import createClientOperationId from '../createClientOperationId';
import { getExpiryDatesBlockers, countProductsToRemoveOnNextService, shouldShowExpiryBeforeNextServiceWarning } from './helpers';
import getScheduledPlanogramItemIds, {
	getProductExpiryScheduleSelectionSeed,
} from './getScheduledPlanogramItemIds';
import { serviceDocketInventoryCheckStatus } from '../vending-settings';
import { shouldMachineBeRestocked } from '../serviceRuns/shouldMachineBeRestocked';
import { checkRestockTypeCondition } from '../serviceRuns/checkRestockTypeCondition';
import mergePlanogramAndPickedExpiryDates from './mergePlanogramAndPickedExpiryDates';
import {
	applyStockReductionReason,
	prepareStockReductionReasonRequest,
} from './stockReductionReason';

const getOrderedKnownExpiryValues = (expiryDates = {}) => {
	return Object.keys(expiryDates || {})
		.map(selection => Number.parseInt(selection, 10))
		.filter(selection => Number.isInteger(selection) && selection > 0)
		.sort((left, right) => left - right)
		.map(selection => expiryDates?.[selection])
		.filter(Boolean);
};

const parseDateValue = (value) => {
	if (!value) {
		return null;
	}

	const parsedDate = new Date(value);
	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getIdString = value => (value ? String(value) : null);

const normalizeExpiryDatesForComparison = (expiryDates = {}) => {
	const normalizedExpiryDates = {};

	Object.keys(expiryDates || {})
		.map(selection => Number.parseInt(selection, 10))
		.filter(selection => Number.isInteger(selection) && selection > 0)
		.sort((left, right) => left - right)
		.forEach((selection) => {
			const parsedDate = parseDateValue(expiryDates?.[selection]);
			if (parsedDate) {
				normalizedExpiryDates[selection] = parsedDate.toISOString();
			}
		});

	return normalizedExpiryDates;
};

const haveExpiryDatesChanged = (leftExpiryDates = {}, rightExpiryDates = {}) => {
	const normalizedLeft = normalizeExpiryDatesForComparison(leftExpiryDates);
	const normalizedRight = normalizeExpiryDatesForComparison(rightExpiryDates);
	const comparisonKeys = [...new Set([
		...Object.keys(normalizedLeft),
		...Object.keys(normalizedRight),
	])];

	return comparisonKeys.some(key => normalizedLeft[key] !== normalizedRight[key]);
};

const getNumberOfRecommendedProductsToRemove = ({
	tenantSettings,
	machine,
	product,
	planogramItemId,
	expiryDates,
	referenceDate,
	timezone,
	isMarket,
}) => {
	if (!shouldShowExpiryBeforeNextServiceWarning({ tenantSettings, product })) {
		return 0;
	}

	return countProductsToRemoveOnNextService(
		machine.planogramItemsVendRate?.[planogramItemId],
		machine.averageDaysSinceLastService,
		expiryDates,
		{ referenceDate, timezone, isMarket },
	);
};

const useConfirmStockLevels = ({
	confirmStockLevelsMutation,
	docket,
	setAnyUnsavedChanges,
	docketId,
	setConfirmStockLevels,
	isFullByDefault,
	tenantSettings,
	timezone,
	mutationHook,
	mutationHookOptions,
	reportMutationHook,
	reportMutationHookOptions,
	onReportIncorrectStockSetting,
	refetch,
	enableStockReductionReasonPrompt = false,
	restockCorrectionStockReviewRevision,
	confirmationBlockedMessage,
	adminTraining = false,
}) => {
	const { values: stockChanges, changeFormValue, resetForm } = useForm({});

	const [isRefreshing, setIsRefreshing] = useState(false);

	const [editedPickListItemId, setEditedPickListItemId] = useState(null);
	const [openEditDialog, setOpenEditDialog] = useState(null);
	const [expiryDateItemKey, setExpiryDateItemKey] = useState(null);
	const [expiryDateSelectorDate, setExpiryDateSelectorDate] = useState();
	const [showExpiryDatesDialogWarning, setShowExpiryDatesDialogWarning] = useState(false);
	const [blockConfirmAnyway, setBlockConfirmAnyway] = useState(false);
	const [pendingStockReductionReasons, setPendingStockReductionReasons] = useState([]);
	const [reportedStockSettings, setReportedStockSettings] = useState({});
	const stockReductionReasonResolverRef = useRef(null);
	const stockReductionReasonPromiseRef = useRef(null);
	const stockReviewSubmissionRef = useRef(null);

	// AVGDSLS is avg days since last service
	const [productNamesToFillExpiryDates, setProductNamesToFillExpiryDates] = useState({ notFilled: [], expirySoon: [], expired: [], expirySoonerThanAVGDSLS: [] });


	const { machine, inventoryCheck } = docket;
	const expiryReferenceDate = docket?.date || docket?.planned;
	const pickListItems = useMemo(() => (
		shouldMachineBeRestocked(docket.machineServiceType)
			? (docket.pickList?.pickListItems || [])
			: (docket.stockCheckItems || [])
	), [docket.machineServiceType, docket.pickList?.pickListItems, docket.stockCheckItems]);
	const isMarket = machine.type === POS_TYPE.MARKET;
	const inventoryAlreadyConfirmed = [
		serviceDocketInventoryCheckStatus.CONFIRMED,
		serviceDocketInventoryCheckStatus.CONFIRMED_WITH_CHANGES,
	].includes(inventoryCheck);

	// combine expiryCheckSchedules active for this docket
	const productExpirySchedules = useMemo(() => {
		const schedulesById = new Map();
		[
			...(docket.productExpiryCheckSchedules || []),
			...(docket.checkedProductExpirySchedules || []),
		].forEach(schedule => {
			const scheduleId = getIdString(schedule?.id || schedule?._id);
			if (scheduleId && !schedulesById.has(scheduleId)) {
				schedulesById.set(scheduleId, schedule);
			}
		});
		return Array.from(schedulesById.values());
	}, [docket.checkedProductExpirySchedules, docket.productExpiryCheckSchedules]);
	const productExpiryScheduleIds = useMemo(
		() => productExpirySchedules.map(schedule => getIdString(schedule?.id || schedule?._id)).filter(Boolean),
		[productExpirySchedules],
	);
	const planogramItemPositionsById = useMemo(() => {
		const planogramItems = machine.planogram?.items || [];
		if (!planogramItems.length) {
			return new Map();
		}

		const positionedPlanogramItems = calculatePlanogramItemsPositions({
			items: planogramItems,
			planogramItemGroups: machine.planogram?.planogramItemGroups || [],
		});

		return new Map(positionedPlanogramItems.map(item => [getIdString(item.id), item.position]));
	}, [machine.planogram?.items, machine.planogram?.planogramItemGroups]);

	// Get enabled planogram items
	const initialConfirmStockLevelsItems = useMemo(() => {
		const initialItems = pickListItems
			// A row whose planogram item no longer exists has nothing to confirm (the web docket skips it too).
			.filter(item => item?.planogramItem?.enabled)
			.map(item => {
			// Use post-inventory quantity only once inventory is confirmed.
			// This avoids stale post-inventory values overriding the latest post-service quantity.
				const presentProductQuantity = (
					inventoryAlreadyConfirmed && valueExists(item.planogramItemQuantityPostInventoryChange)
						? item.planogramItemQuantityPostInventoryChange
						: item.planogramItemQuantityPostService ?? item.planogramItem?.quantity ?? (isFullByDefault ? item.planogramItem.parlevel : null)
				);

				const planogramExpiryDates = item.planogramItem?.expiryDates || {};
				const expiryDatesAtPick = valueExists(item.planogramItemExpiryDatesAtPick)
					? (item.planogramItemExpiryDatesAtPick || {})
					: {};
				const expiryDatesPostConfirmation = valueExists(item.planogramItemExpiryDatesPostConfirmation)
					? (item.planogramItemExpiryDatesPostConfirmation || {})
					: null;
				const existingExpiryDates = inventoryAlreadyConfirmed
					? (expiryDatesPostConfirmation || planogramExpiryDates)
					: mergePlanogramAndPickedExpiryDates({
						planogramExpiryDates,
						pickedExpiryDates: expiryDatesAtPick,
						planogramItemQuantityPreService: item.planogramItemQuantityPreService,
						quantity: presentProductQuantity,
						isMarket,
					});

				let expiryDates = getAdjustedExpiryDatesByQuantityChange(existingExpiryDates, presentProductQuantity, isMarket);
				// if product was removed/changed clear the expiry dates
				// do not clear dates if inventory check is confirmed
				if (item.removedProduct && (docket.inventoryCheck === serviceDocketInventoryCheckStatus.REQUESTED || docket.inventoryCheck === serviceDocketInventoryCheckStatus.PENDING)) {
					expiryDates = {};
				}

				const newItem = {
					planogramItemId: item.planogramItem.id,
					...item.planogramItem,
					position: planogramItemPositionsById.get(getIdString(item.planogramItem.id)) ?? item.planogramItem.position,
					planogramExpiryDates,
					existingExpiryDates,
					expiryDatesAutoSorted: false,
					suggestedExpiryDatesFromWarehouse: item.suggestedExpiryDatesFromWarehouse || null,
					removedProduct: item.removedProduct,
					productQuantityPreService: item.planogramItemQuantityPreService ?? 0,
					initialPresentProductQuantity: presentProductQuantity,
					presentProductQuantity,
					productName: item?.planogramItem?.product?.name,
					spoiledProductQuantity: item.spoiledProductQuantity,
					removedProductQuantity: item.removedProductQuantity,
					salesLast365Days: item.planogramItemStatistics?.salesLast365Days,
					salesLast30Days: item.planogramItemStatistics?.salesLast30Days,
					salesLast14Days: item.planogramItemStatistics?.salesLast14Days,
					salesLast7Days: item.planogramItemStatistics?.salesLast7Days,
					lastProductChangeTime: item.planogramItemStatistics?.lastProductChangeTime,
					expiryDates,
					id: item.id,
				};

				if (tenantSettings.useExpiryDates) {
				// how many items will be sold until next service
					newItem.numberOfRecommendedProductsToRemove = getNumberOfRecommendedProductsToRemove({
						tenantSettings,
						machine,
						product: newItem.product,
						planogramItemId: newItem.planogramItemId,
						expiryDates: newItem.expiryDates,
						referenceDate: expiryReferenceDate,
						timezone,
						isMarket,
					});
				}

				return newItem;
			});

		if (!tenantSettings.useExpiryDates) {
			return initialItems;
		}

		const scheduleItems = initialItems
			.filter(item => (
				!docket.machineRestockType ||
				checkRestockTypeCondition({
					restockType: docket.machineRestockType,
					product: item.product,
				})
			))
			.map(item => ({
				planogramItemId: item.planogramItemId,
				productId: item.product?.id,
				productTagIds: item.product?.tags?.map(t => t.id),
				isFreshFood: item.product?.isFreshFood,
				isMarket,
				quantity: item.presentProductQuantity,
				presentProductQuantity: item.presentProductQuantity,
				expiryDates: item.expiryDates,
				productExpiryScheduleLastCheckedAt: item.productExpiryScheduleLastCheckedAt,
				lastProductChangeTime: item.lastProductChangeTime,
				planogramItemVendRate: machine.planogramItemsVendRate?.[item.planogramItemId],
			}));
		const schedulePlannedDate = docket?.planned || docket?.serviceRun?.planned || docket?.date;
		const scheduleSelectionSeed = getProductExpiryScheduleSelectionSeed({
			machineId: machine.id || machine._id,
			plannedDate: schedulePlannedDate,
			timezone,
		});
		const scheduledPlanogramItemIds = productExpirySchedules.reduce((acc, schedule) => {
			const selectedPlanogramItemIds = getScheduledPlanogramItemIds({
				items: scheduleItems,
				productExpiryCheckSchedules: [schedule],
				seed: scheduleSelectionSeed,
				plannedDate: schedulePlannedDate,
				expiryDateThresholdDays: tenantSettings.expiryDateThresholds?.[0],
				averageDaysSinceLastService: machine.averageDaysSinceLastService,
				planogramItemsVendRate: machine.planogramItemsVendRate,
				timezone,
			});
			selectedPlanogramItemIds.forEach(planogramItemId => acc.add(planogramItemId));
			return acc;
		}, new Set());

		return initialItems.map(item => ({
			...item,
			productInSchedule: Boolean(item.numberOfRecommendedProductsToRemove) || scheduledPlanogramItemIds.has(item.planogramItemId),
		}));
	}, [
		pickListItems,
		isFullByDefault,
		inventoryAlreadyConfirmed,
		docket.inventoryCheck,
		docket.planned,
		docket.date,
		docket.serviceRun?.planned,
		docket.machineRestockType,
		expiryReferenceDate,
		tenantSettings,
		timezone,
		productExpirySchedules,
		machine,
		isMarket,
		planogramItemPositionsById,
	]);

	const [confirmStockLevelsItems, setConfirmStockLevelsItems] = useState(initialConfirmStockLevelsItems);
	const hasPendingStockChanges = Object.keys(stockChanges).length > 0;

	useEffect(() => {
		// Keep stale cache values from sticking when fresh network data arrives.
		// Preserve rows the driver already edited, but refresh untouched rows.
		if (isRefreshing) {
			return;
		}

		setConfirmStockLevelsItems(items => {
			if (!hasPendingStockChanges) {
				return items === initialConfirmStockLevelsItems ? items : initialConfirmStockLevelsItems;
			}

			const currentItemsById = new Map(items.map(item => [item.id, item]));

			return initialConfirmStockLevelsItems.map(initialItem => (
				stockChanges[initialItem.id] && currentItemsById.has(initialItem.id)
					? currentItemsById.get(initialItem.id)
					: initialItem
			));
		});
	}, [hasPendingStockChanges, initialConfirmStockLevelsItems, isRefreshing, stockChanges]);

	useDidUpdateEffect(() => {
		setConfirmStockLevelsItems(items => items.map(item => {
			if (stockChanges[item.id]) {

				for (const [key, value] of Object.entries(stockChanges[item.id])) {
					if (item[key] !== value) {
						return { ...item, ...stockChanges[item.id] };
					}
				}
			}
			return item;
		}));
	}, [setConfirmStockLevelsItems, stockChanges]);

	const mutationImpl = mutationHook || useMutation;
	const [confirmStockLevelsMutationRaw, { loading }] = mutationImpl(
		confirmStockLevelsMutation || CONFIRM_STOCK_LEVELS,
		mutationHookOptions,
	);

	const reportMutationImpl = reportMutationHook || useMutation;
	const [reportIncorrectStockSettingMutation, { loading: reportingIncorrectStockSetting }] = reportMutationImpl(
		REPORT_INCORRECT_STOCK_SETTING,
		reportMutationHookOptions,
	);

	const isStockSettingReported = useCallback((itemId, setting) => (
		Boolean(reportedStockSettings[itemId]?.[String(setting || '').toUpperCase()])
	), [reportedStockSettings]);

	const hasReportedStockSetting = useCallback(itemId => (
		Boolean(reportedStockSettings[itemId] && Object.values(reportedStockSettings[itemId]).some(Boolean))
	), [reportedStockSettings]);

	const reportIncorrectStockSetting = useEventCallback(async ({ itemId, setting, suggestedValue, detail }) => {
		const normalizedSetting = String(setting || '').toUpperCase();
		if (reportedStockSettings[itemId]?.[normalizedSetting]) {
			return { alreadyReported: true };
		}

		const reportItem = confirmStockLevelsItems.find(item => item.id === itemId);

		const result = await reportIncorrectStockSettingMutation({
			variables: {
				serviceDocketId: docketId,
				planogramItemId: reportItem?.planogramItemId || itemId,
				setting: normalizedSetting,
				...(suggestedValue === null || suggestedValue === undefined || suggestedValue === ''
					? {}
					: { suggestedValue: Number(suggestedValue) }),
				...(detail ? { detail } : {}),
			},
			context: {
				offlineMeta: {
					machineDetails: machine,
					machineName: getFullMachineName(machine, machine.place, machine.location),
					machineId: machine?.id,
				},
			},
		});

		setReportedStockSettings((previous) => ({
			...previous,
			[itemId]: {
				...previous[itemId],
				[normalizedSetting]: true,
			},
		}));

		return result;
	});

	const confirmStockLevels = useCallback(finalStockChanges => {
		const hasStockReductionClassification = Object.values(finalStockChanges || {}).some(changes => (
			Boolean(changes?.stockReductionClassification)
		));
		const stockReviewRevision = restockCorrectionStockReviewRevision === undefined ||
			restockCorrectionStockReviewRevision === null
			? null
			: Number(restockCorrectionStockReviewRevision);
		const stockReviewSignature = stockReviewRevision === null ? null : JSON.stringify({
			serviceDocketId: docketId,
			stockChanges: finalStockChanges,
			checkedProductExpirySchedules: productExpiryScheduleIds,
			expectedRestockRevision: Number(docket.restockRevision || 0),
			restockCorrectionStockReviewRevision: stockReviewRevision,
		});
		const previousStockReviewSubmission = stockReviewSignature &&
			stockReviewSubmissionRef.current?.signature === stockReviewSignature
			? stockReviewSubmissionRef.current
			: null;
		const operationId = previousStockReviewSubmission
			? previousStockReviewSubmission.operationId
			: createClientOperationId(stockReviewSignature ? 'market-restock-stock-review' : 'stock-confirmation');
		const confirmedAt = hasStockReductionClassification
			? previousStockReviewSubmission?.confirmedAt || new Date().toISOString()
			: null;
		if (stockReviewSignature) {
			// The server fingerprints the confirmation time with the operation identity,
			// so an unchanged retry must reuse both values.
			stockReviewSubmissionRef.current = {
				signature: stockReviewSignature,
				operationId,
				confirmedAt,
			};
		}

		return confirmStockLevelsMutationRaw({
			variables: {
				serviceDocketId: docketId,
				stockChanges: finalStockChanges,
				checkedProductExpirySchedules: productExpiryScheduleIds,
				operationId,
				expectedRestockRevision: Number(docket.restockRevision || 0),
				...(stockReviewRevision === null ? {} : {
					restockCorrectionStockReviewRevision: stockReviewRevision,
				}),
				...(confirmedAt ? {
					confirmedAt,
				} : {}),
				...(adminTraining ? { adminTraining: true } : {}),
			},
			context: {
				offlineMeta: {
					machineDetails: machine,
					machineName: getFullMachineName(machine, machine.place, machine.location),
					machineId: machine?.id,
				},
			},
		});
	}, [adminTraining, confirmStockLevelsMutationRaw, docket.restockRevision, docketId, productExpiryScheduleIds, machine, restockCorrectionStockReviewRevision]);

	const requestStockReductionReasons = useCallback((requests) => {
		if (!stockReductionReasonPromiseRef.current) {
			stockReductionReasonPromiseRef.current = new Promise(resolve => {
				stockReductionReasonResolverRef.current = resolve;
				setPendingStockReductionReasons(requests);
			});
		}

		return stockReductionReasonPromiseRef.current;
	}, []);

	const resolveStockReductionReasons = useEventCallback((selectedReasons) => {
		const resolve = stockReductionReasonResolverRef.current;
		stockReductionReasonResolverRef.current = null;
		stockReductionReasonPromiseRef.current = null;
		setPendingStockReductionReasons([]);
		resolve?.(selectedReasons);
	});

	const skipStockReductionReasons = useEventCallback(() => {
		resolveStockReductionReasons(null);
	});

	useEffect(() => () => {
		stockReductionReasonResolverRef.current?.({ confirmationAborted: true });
		stockReductionReasonResolverRef.current = null;
		stockReductionReasonPromiseRef.current = null;
	}, []);

	useEffect(() => {
		if (!isRefreshing || refetch) {
			return;
		}

		resetForm();
		setAnyUnsavedChanges(false);
		setConfirmStockLevelsItems(initialConfirmStockLevelsItems);
		setIsRefreshing(false);
	}, [initialConfirmStockLevelsItems, isRefreshing, refetch, resetForm, setAnyUnsavedChanges]);

	useEffect(() => {
		if (!isRefreshing || !refetch) {
			return;
		}

		let isCancelled = false;

		resetForm();
		setAnyUnsavedChanges(false);

		const refreshStockLevels = async () => {
			try {
				await refetch();
			}
			catch (error) {
				console.log('Error refreshing stock levels', error);
			}
			finally {
				if (!isCancelled) {
					setIsRefreshing(false);
				}
			}
		};

		refreshStockLevels();

		return () => {
			isCancelled = true;
		};
	}, [isRefreshing, refetch, resetForm, setAnyUnsavedChanges]);

	const changeStockItem = useEventCallback((itemId, field) => value => {

		const updatedExpiryDatesObj = {};
		let confirmStockLevelItem;

		// When the quantity (spoilage, removal, or present) is changed and the product already has expiry dates, prompt the user to update the expiry dates.
		if (!isMarket && tenantSettings.useExpiryDates && ['spoiledProductQuantity', 'presentProductQuantity', 'removedProductQuantity'].includes(field)) {
			confirmStockLevelItem = confirmStockLevelsItems.find(item => item.id === itemId);

			const anyQuantityChanged = checkIfAnyQuantityChanged(confirmStockLevelItem, stockChanges[itemId], field, value);

			if (anyQuantityChanged && getOrderedKnownExpiryValues(confirmStockLevelItem.expiryDates).length > 0) {
				// if user update quantity then reset dates
				updatedExpiryDatesObj.expiryDates = value ? getAdjustedExpiryDatesByQuantityChange({}, field === 'presentProductQuantity' ? value : confirmStockLevelItem.presentProductQuantity, isMarket) : {};
				updatedExpiryDatesObj.expiryDatesAutoSorted = false;
				updatedExpiryDatesObj.productInSchedule = true;
				updatedExpiryDatesObj.numberOfRecommendedProductsToRemove = 0;
			}
			else if (!anyQuantityChanged) {
				// restre dates if any of the quantities where not changed
				// e.g. user set spoiled to 1 and then set spoiled to 0;
				const dateToRestore = getOrderedKnownExpiryValues(stockChanges[itemId]?.expiryDates).length > 0
					? stockChanges[itemId].expiryDates
					: confirmStockLevelItem.existingExpiryDates;
				const restoredExpiryDates = getAdjustedExpiryDatesByQuantityChange(
					dateToRestore,
					field === 'presentProductQuantity' ? value : confirmStockLevelItem.initialPresentProductQuantity,
					isMarket,
				);
				const sortedExpiryDates = sortExpiryDatesByDate(
					restoredExpiryDates,
					field === 'presentProductQuantity' ? value : confirmStockLevelItem.initialPresentProductQuantity,
					isMarket,
				);
				const hasAutoSortedExpiryDates = stockChanges[itemId]?.expiryDatesAutoSorted ||
					confirmStockLevelItem.expiryDatesAutoSorted ||
					sortedExpiryDates.wasReordered;
				updatedExpiryDatesObj.expiryDates = sortedExpiryDates.expiryDates;
				updatedExpiryDatesObj.expiryDatesAutoSorted = Boolean(hasAutoSortedExpiryDates);
				updatedExpiryDatesObj.numberOfRecommendedProductsToRemove = getNumberOfRecommendedProductsToRemove({
					tenantSettings,
					machine,
					product: confirmStockLevelItem.product,
					planogramItemId: confirmStockLevelItem.planogramItemId,
					expiryDates: updatedExpiryDatesObj.expiryDates,
					referenceDate: expiryReferenceDate,
					timezone,
					isMarket,
				});
			}
		}

		// if user update expiry dates after product quantity(ies) change
		// set anyQuantityChanged to false to not show warning dialog
		if (field === 'expiryDates') {
			confirmStockLevelItem = confirmStockLevelsItems.find(item => item.id === itemId);

			let nextExpiryDates;
			const hasSingleSelectionPayload = value && typeof value === 'object' && value.selection;
			if (hasSingleSelectionPayload) {
				const currentExpiryDates = valueExists(stockChanges[itemId]?.expiryDates) ? stockChanges[itemId].expiryDates : confirmStockLevelItem.expiryDates;
				nextExpiryDates = updateExpiryDates(currentExpiryDates, value, confirmStockLevelItem.presentProductQuantity, isMarket);
			}
			else {
				nextExpiryDates = getAdjustedExpiryDatesByQuantityChange(value || {}, confirmStockLevelItem.presentProductQuantity, isMarket);
			}
			const sortedExpiryDates = sortExpiryDatesByDate(nextExpiryDates, confirmStockLevelItem.presentProductQuantity, isMarket);
			const hasAutoSortedExpiryDates = stockChanges[itemId]?.expiryDatesAutoSorted ||
				confirmStockLevelItem.expiryDatesAutoSorted ||
				sortedExpiryDates.wasReordered;
			updatedExpiryDatesObj.expiryDates = sortedExpiryDates.expiryDates;
			updatedExpiryDatesObj.expiryDatesAutoSorted = Boolean(hasAutoSortedExpiryDates);
			updatedExpiryDatesObj.productInSchedule = true;
			updatedExpiryDatesObj.numberOfRecommendedProductsToRemove = getNumberOfRecommendedProductsToRemove({
				tenantSettings,
				machine,
				product: confirmStockLevelItem.product,
				planogramItemId: confirmStockLevelItem.planogramItemId,
				expiryDates: updatedExpiryDatesObj.expiryDates,
				referenceDate: expiryReferenceDate,
				timezone,
				isMarket,
			});
		}

		const nextItemStockChanges = {
			...stockChanges[itemId],
		};
		if ([
			'presentProductQuantity',
			'spoiledProductQuantity',
			'removedProductQuantity',
		].includes(field)) {
			delete nextItemStockChanges.stockReductionClassification;
		}

		changeFormValue({
			...stockChanges,
			[itemId]: {
				...nextItemStockChanges,
				[field]: value,
				...updatedExpiryDatesObj,
			},
		});
		setAnyUnsavedChanges(true);
	});

	const onClickPlanogramItem = useCallback(pickListItemId => () => {
		setEditedPickListItemId(pickListItemId);
		setOpenEditDialog(true);
	}, [setEditedPickListItemId]);

	const onClickExpiryDateButton = useCallback((pickListItemId, expiryDateItem, date) => () => {
		setEditedPickListItemId(pickListItemId);
		setExpiryDateItemKey(expiryDateItem);
		setExpiryDateSelectorDate(date);
	}, [setEditedPickListItemId, setExpiryDateItemKey]);

	const onPressConfirmStockLevels = useCallback(async (force) => {
		if (confirmationBlockedMessage) {
			throw new Error(confirmationBlockedMessage);
		}

		if (tenantSettings.useExpiryDates) {

			const { blockConfirmAnyway, showExpiryDatesDialogWarning, productNamesToFillExpiryDates } = shouldBlockConfirmation({
				force,
				machine,
				tenantSettings,
				confirmStockLevelsItems,
				machineRestockType: docket.machineRestockType,
				referenceDate: expiryReferenceDate,
				timezone,
			});

			const showWarning = showExpiryDatesDialogWarning || blockConfirmAnyway;
			// show warning dialog and block confirmation (if necessary)
			if (showWarning) {
				setShowExpiryDatesDialogWarning(showExpiryDatesDialogWarning || blockConfirmAnyway);
				setBlockConfirmAnyway(blockConfirmAnyway);
				setProductNamesToFillExpiryDates(productNamesToFillExpiryDates);
				return { showExpiryDatesDialogWarning: showWarning, blockConfirmAnyway, productNamesToFillExpiryDates };
			}
		}
		let resolvedStockChanges = stockChanges;
		if (
			enableStockReductionReasonPrompt &&
			!inventoryAlreadyConfirmed &&
			shouldMachineBeRestocked(docket.machineServiceType)
		) {
			const reasonRequests = confirmStockLevelsItems
				.filter(item => !stockChanges[item.id]?.stockReductionClassification)
				.map(item => prepareStockReductionReasonRequest(item, stockChanges[item.id]))
				.filter(Boolean);

			if (reasonRequests.length) {
				const selectedReasons = await requestStockReductionReasons(reasonRequests);
				if (selectedReasons?.confirmationAborted) {
					return { confirmationAborted: true };
				}

				if (selectedReasons) {
					resolvedStockChanges = reasonRequests.reduce((nextStockChanges, request) => ({
						...nextStockChanges,
						[request.itemId]: applyStockReductionReason({
							changes: nextStockChanges[request.itemId],
							request,
							reason: selectedReasons[request.itemId],
						}),
					}), { ...stockChanges });
					changeFormValue(resolvedStockChanges);
					setAnyUnsavedChanges(true);
				}
			}
		}

		// flatten products
		const finalValues = Object.keys(resolvedStockChanges).reduce((acc, pickListItemId) => {
			acc[pickListItemId] = {
				...resolvedStockChanges[pickListItemId],
				...(resolvedStockChanges[pickListItemId].product ? { product: resolvedStockChanges[pickListItemId].product.id } : {}),
			};

			return acc;
		}, {});

		if (tenantSettings.useExpiryDates) {
			confirmStockLevelsItems.forEach(item => {
				const currentExpiryDates = valueExists(resolvedStockChanges[item.id]?.expiryDates)
					? resolvedStockChanges[item.id].expiryDates
					: item.expiryDates;
				const planogramExpiryDates = item.planogramExpiryDates || {};

				if (!haveExpiryDatesChanged(currentExpiryDates, planogramExpiryDates)) {
					return;
				}

				finalValues[item.id] = {
					...finalValues[item.id],
					expiryDates: currentExpiryDates || {},
				};
			});
		}

		return confirmStockLevels(finalValues).then(result => {
			setAnyUnsavedChanges(false);
			return result;
		});
	}, [
		machine,
		tenantSettings,
		confirmStockLevelsItems,
		stockChanges,
		changeFormValue,
		setAnyUnsavedChanges,
		confirmStockLevels,
		docket.machineRestockType,
		docket.machineServiceType,
		expiryReferenceDate,
		timezone,
		enableStockReductionReasonPrompt,
		inventoryAlreadyConfirmed,
		requestStockReductionReasons,
		confirmationBlockedMessage,
	]);

	// Warehouse suggestions describe stock still in the warehouse. During confirmation we
	// only auto-fill dates from persisted machine/picked snapshots, not from remaining stock.

	// pass the confirm action to parent
	useEffect(() => {
		if (setConfirmStockLevels) {
			setConfirmStockLevels(() => onPressConfirmStockLevels);
		}
	}, [onPressConfirmStockLevels, setConfirmStockLevels]);

	const confirmStockLevelsContextValues = useMemo(() => ({
		changeStockItem,
		expiryReferenceDate,
		isMarket,
		onClickExpiryDateButton,
		timezone,
		onReportIncorrectStockSetting,
		hasReportedStockSetting,
		isStockSettingReported,
		showCapacity: Boolean(tenantSettings?.enablePlanogramItemCapacity),
	}), [
		changeStockItem,
		expiryReferenceDate,
		isMarket,
		onClickExpiryDateButton,
		timezone,
		onReportIncorrectStockSetting,
		hasReportedStockSetting,
		isStockSettingReported,
		tenantSettings?.enablePlanogramItemCapacity,
	]);

	const machineUsesIngredients = Boolean(machine.ingredientPlanogram);
	const machineName = useMemo(() => getFullMachineName(machine, machine.place, machine.location), [machine]);

	return ({
		confirmStockLevelsItems,
		loading,
		openEditDialog,
		setOpenEditDialog,
		onClickPlanogramItem,
		onClickExpiryDateButton,
		confirmStockLevelsContextValues,
		machineUsesIngredients,
		isMarket,
		machineName,
		isRefreshing,
		setIsRefreshing,
		editedPickListItemId,
		setEditedPickListItemId,
		inventoryCheck,
		onPressConfirmStockLevels,
		changeStockItem,
		expiryDateItemKey,
		setExpiryDateItemKey,
		showExpiryDatesDialogWarning,
		setShowExpiryDatesDialogWarning,
		blockConfirmAnyway,
		setBlockConfirmAnyway,
		productNamesToFillExpiryDates,
		expiryDateSelectorDate,
		setExpiryDateSelectorDate,
		pendingStockReductionReasons,
		resolveStockReductionReasons,
		skipStockReductionReasons,
		reportIncorrectStockSetting,
		isStockSettingReported,
		hasReportedStockSetting,
		reportingIncorrectStockSetting,
	});
};

export default useConfirmStockLevels;

export const includesAny = (arr, values) => values?.some(v => arr.includes(v));

export const checkIfAnyQuantityChanged = (initialConfirmStockLevelItem, confirmStockLevelItem, field, value) => {
	switch (field) {
		case 'presentProductQuantity': {
			return Boolean(initialConfirmStockLevelItem?.initialPresentProductQuantity !== value || confirmStockLevelItem?.removedProductQuantity || confirmStockLevelItem?.spoiledProductQuantity);
		}
		case 'removedProductQuantity': {
			return Boolean(value || (confirmStockLevelItem?.presentProductQuantity && initialConfirmStockLevelItem?.initialPresentProductQuantity !== confirmStockLevelItem?.presentProductQuantity) || confirmStockLevelItem?.spoiledProductQuantity);
		}
		case 'spoiledProductQuantity': {
			return Boolean(value || (confirmStockLevelItem?.presentProductQuantity && initialConfirmStockLevelItem?.initialPresentProductQuantity !== confirmStockLevelItem?.presentProductQuantity) || confirmStockLevelItem?.removedProductQuantity);
		}
	}

	return false;
};

const shouldBlockConfirmation = ({
	force,
	machine,
	tenantSettings,
	confirmStockLevelsItems,
	machineRestockType,
	referenceDate,
	timezone,
}) => {

	let showExpiryDatesDialogWarning = false; // show warning button
	let blockConfirmAnyway = false; // if true then do not display "confirm anyway" button

	// AVGDSLS is avg days since last service
	const productNamesToFillExpiryDates = { notFilled: [], expirySoon: [], expired: [], expirySoonerThanAVGDSLS: [] };

	if (!force && !machine.isOffline && tenantSettings?.useExpiryDates) {


		confirmStockLevelsItems.forEach(item => {
			if (!item.presentProductQuantity) {
				return;
			}
			if (
				machineRestockType &&
				!checkRestockTypeCondition({ restockType: machineRestockType, product: item.product })
			) {
				return;
			}

			// create expiryDatesArray using presentProductQuantity
			const expiryDatesArray = [...Array(machine.type === POS_TYPE.MARKET ? 1 : item.presentProductQuantity)].map((_, i) => item.expiryDates[i + 1]);

			const {
				expiredProductsCount,
				expireTodayCount,
				hasExpiryDatesNotFilledCount,
				productsExpiringWithinXDaysCount,
				productsPlacedInIncorrectOrder,
			} = getExpiryDatesBlockers(expiryDatesArray, tenantSettings, {
				referenceDate,
				timezone,
			});

			// no matter what: if the product has expired or expires today, block confirm stock levels and prompt the user to update expiry dates
			// create array with product names that expired
			if (expiredProductsCount || expireTodayCount) {
				// this will block confirmation unless user update dates
				productNamesToFillExpiryDates.expired.push(item.productName);
				showExpiryDatesDialogWarning = true;
				blockConfirmAnyway = true;
			}
			// create array with product names with unknown expiry dates
			else if (productsPlacedInIncorrectOrder) {
				// block confirmation unless user updates dates that are in the wrong order
				productNamesToFillExpiryDates.notFilled.push(item.productName);
				showExpiryDatesDialogWarning = true;
				blockConfirmAnyway = true;
			}
			else if (item.productInSchedule && hasExpiryDatesNotFilledCount) {
				productNamesToFillExpiryDates.notFilled.push(item.productName);
				showExpiryDatesDialogWarning = true;
			}

			if (!expiredProductsCount && !expireTodayCount) {

				// if product is not expired or expires today and expires sooner than avg DSLS, show warning
				if (item.numberOfRecommendedProductsToRemove) {
					productNamesToFillExpiryDates.expirySoonerThanAVGDSLS.push(item.productName);
					showExpiryDatesDialogWarning = true;
				}
				else if (productsExpiringWithinXDaysCount) {
					// it will allow confirmation but show dialog with "confirm anyway" button
					productNamesToFillExpiryDates.expirySoon.push(item.productName);
					showExpiryDatesDialogWarning = true;
				}
			}
		});
	}

	// 1. blockConfirmAnyway: true
	// after click "confirm stock levels" user will get dialog with
	// warning that he CANNOT confirm unless fill required dates
	// 2. showExpiryDatesDialogWarning: true
	// just show warning dialog but allow user to "confirm anyway"
	return {
		showExpiryDatesDialogWarning,
		blockConfirmAnyway,
		productNamesToFillExpiryDates,
	};

};
